import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, MapPin, Filter, Shield } from "lucide-react";
import { SCHEDULE, TOURNAMENT_INFO, TEAMS } from "../data/tournament";
import { api } from "../lib/api";
import { resolveGameTeams, computeStandings } from "../lib/standings";
import LiveBadge from "../components/LiveBadge";

const TEAM_ID_BY_NAME = TEAMS.reduce((m, t) => { m[t.name] = t.id; return m; }, {});

const DAYS = ["Vendredi 1 Mai", "Samedi 2 Mai", "Dimanche 3 Mai"];
const ICES = ["Toutes", ...TOURNAMENT_INFO.ices];
const CATS = ["Toutes", "2017", "2018"];

const Horaire = () => {
  const [day, setDay] = useState("Tous");
  const [cat, setCat] = useState("Toutes");
  const [ice, setIce] = useState("Toutes");
  const [scores, setScores] = useState([]);
  const [teamsInfo, setTeamsInfo] = useState([]);

  useEffect(() => {
    api.get("/scores").then((r) => setScores(r.data)).catch(() => setScores([]));
    api.get("/teams").then((r) => setTeamsInfo(r.data)).catch(() => setTeamsInfo([]));
  }, []);

  const logoByTeamId = useMemo(() => {
    const m = {};
    teamsInfo.forEach((t) => {
      if (t.logo_base64) m[t.team_id] = t.logo_base64;
    });
    return m;
  }, [teamsInfo]);

  const getLogo = (teamName) => {
    const id = TEAM_ID_BY_NAME[teamName];
    return id ? logoByTeamId[id] : null;
  };

  const scoreByGame = useMemo(() => {
    const m = {};
    scores.forEach((s) => {
      m[s.game_num] = s;
    });
    return m;
  }, [scores]);

  // Live standings per category — used to resolve playoff placeholders ("Pool A position 3", etc.)
  // Only populated once ALL preliminary games of the category are marked "final".
  // Until then, placeholders stay unresolved and are shown in italic.
  const standingsByCat = useMemo(() => {
    const out = {};
    ["2017", "2018"].forEach((cat) => {
      const prelim = SCHEDULE.filter((g) => g.category === cat && !g.phase);
      const allDone =
        prelim.length > 0 && prelim.every((g) => scoreByGame[g.num]?.status === "final");
      if (!allDone) return; // leave undefined → resolveOne returns placeholder
      const teams = TEAMS.filter((t) => t.category === cat).map((t) => t.name);
      out[cat] = computeStandings(scoreByGame, teams);
    });
    return out;
  }, [scoreByGame]);

  const filtered = useMemo(() => {
    return SCHEDULE.filter((g) => {
      if (day !== "Tous" && g.day !== day) return false;
      if (cat !== "Toutes" && g.category !== cat) return false;
      if (ice !== "Toutes" && g.ice !== ice) return false;
      return true;
    });
  }, [day, cat, ice]);

  const grouped = useMemo(() => {
    const out = {};
    filtered.forEach((g) => {
      if (!out[g.day]) out[g.day] = [];
      out[g.day].push(g);
    });
    return out;
  }, [filtered]);

  return (
    <div className="bg-[#0a0a0a] text-white min-h-screen" data-testid="horaire-page">
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 diagonal-stripes opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-10 pb-8 sm:pt-16 sm:pb-12 lg:py-20">
          <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">
            Coupe CH · 1·2·3 Mai 2026
          </span>
          <h1 className="mt-2 sm:mt-3 font-display text-4xl sm:text-6xl lg:text-8xl uppercase leading-none">
            Horaire <span className="text-[#E60012]">complet</span>
          </h1>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-4 sm:py-6 lg:py-8">
        <div className="bg-[#111] border border-white/10 p-4 sm:p-5 space-y-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-white/70 font-condensed uppercase tracking-[0.2em] text-xs">
              <Filter className="w-4 h-4 text-[#E60012]" /> Filtres
            </div>
            <div className="font-condensed uppercase tracking-[0.2em] text-xs text-white/60">
              {filtered.length} partie{filtered.length > 1 ? "s" : ""}
            </div>
          </div>

          <FilterGroup label="Jour" options={["Tous", ...DAYS]} value={day} onChange={setDay} testid="filter-day" />
          <FilterGroup label="Catégorie" options={CATS} value={cat} onChange={setCat} testid="filter-category" />
          <FilterGroup label="Glace" options={ICES} value={ice} onChange={setIce} testid="filter-ice" />
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20 space-y-12">
        {DAYS.map((d) => {
          const games = grouped[d];
          if (!games || !games.length) return null;
          return (
            <div key={d} data-testid={`day-section-${d.split(" ")[0].toLowerCase()}`}>
              <div className="flex items-center gap-4 mb-5">
                <Calendar className="w-5 h-5 text-[#E60012]" />
                <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl uppercase">{d}</h2>
                <div className="flex-1 h-px bg-white/10" />
                <span className="font-condensed uppercase tracking-[0.2em] text-xs text-white/50">
                  {games.length} parties
                </span>
              </div>

              {/* Desktop table */}
              <div className="hidden md:block border border-white/10 overflow-hidden">
                <div className="grid grid-cols-[60px_90px_180px_70px_1fr_120px_1fr] gap-2 bg-[#E60012] text-white px-4 py-3 font-condensed uppercase tracking-[0.2em] text-xs">
                  <div>#</div>
                  <div>Heure</div>
                  <div>Glace</div>
                  <div>Cat.</div>
                  <div>Visiteur</div>
                  <div className="text-center">Score</div>
                  <div>Local</div>
                </div>
                {games.map((g, i) => {
                  const s = scoreByGame[g.num];
                  const teams = resolveGameTeams(g, scoreByGame, standingsByCat);
                  const isLive = s?.status === "live";
                  return (
                    <motion.div
                      key={g.num}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.02 }}
                      className={`relative grid grid-cols-[60px_90px_180px_70px_1fr_120px_1fr] gap-2 px-4 py-4 items-center border-t border-white/5 hover:bg-[#E60012]/10 transition-colors ${
                        isLive ? "bg-red-950/30 ring-1 ring-inset ring-[#E60012]/70 animate-[live-breathe_2.2s_ease-in-out_infinite]" : i % 2 === 0 ? "bg-[#0c0c0c]" : "bg-[#141414]"
                      }`}
                      data-testid={`game-row-${g.num}`}
                    >
                      <Link to={`/match/${g.num}`} className="contents cursor-pointer" data-testid={`game-link-${g.num}`}>
                        <div className="font-display text-2xl text-[#E60012]">{g.num}</div>
                        <div className="font-condensed text-white">{g.time}</div>
                        <div className="text-white/80 text-sm flex items-center gap-2">
                          <MapPin className="w-3.5 h-3.5 text-[#E60012]" /> {g.ice}
                        </div>
                        <div>
                          <span className="px-2 py-1 text-xs font-condensed uppercase tracking-[0.15em] border border-white/20 text-white/70">
                            {g.category}
                          </span>
                        </div>
                        <div className={`font-condensed truncate flex items-center gap-2 ${teams.awayResolved ? "text-white" : "text-white/55 italic"}`}>
                          <TeamLogoSmall logo={teams.awayResolved ? getLogo(teams.away) : null} />
                          <span className="truncate">
                            {teams.away}
                            {teams.awayResolved && g.away !== teams.away && (
                              <span className="block text-[9px] text-white/45 font-condensed uppercase tracking-[0.15em] truncate">
                                {g.away}
                              </span>
                            )}
                          </span>
                        </div>
                        <ScoreCell score={s} />
                        <div className={`font-condensed truncate flex items-center gap-2 ${teams.homeResolved ? "text-white" : "text-white/55 italic"}`}>
                          <TeamLogoSmall logo={teams.homeResolved ? getLogo(teams.home) : null} />
                          <span className="truncate">
                            {teams.home}
                            {teams.homeResolved && g.home !== teams.home && (
                              <span className="block text-[9px] text-white/45 font-condensed uppercase tracking-[0.15em] truncate">
                                {g.home}
                              </span>
                            )}
                          </span>
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3">
                {games.map((g) => {
                  const s = scoreByGame[g.num];
                  const teams = resolveGameTeams(g, scoreByGame, standingsByCat);
                  const isLive = s?.status === "live";
                  return (
                    <Link key={g.num} to={`/match/${g.num}`} className={`block bg-[#111] border p-4 hover:border-[#E60012]/50 transition-colors ${
                      isLive ? "border-[#E60012]/80 bg-red-950/25 animate-[live-breathe_2.2s_ease-in-out_infinite]" : "border-white/10"
                    }`} data-testid={`game-card-${g.num}`}>
                      <div className="flex items-center justify-between text-xs font-condensed uppercase tracking-[0.2em] text-white/60">
                        <span>#{g.num} · {g.time}</span>
                        <span className="text-[#E60012]">{g.category}</span>
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0 flex items-center gap-2 justify-end">
                          <div className="text-right min-w-0">
                            <div className="text-[10px] text-white/40 uppercase tracking-wider">Visiteur</div>
                            <div className={`font-condensed text-sm truncate ${teams.awayResolved ? "text-white" : "text-white/55 italic"}`}>{teams.away}</div>
                            {teams.awayResolved && g.away !== teams.away && (
                              <div className="text-[9px] text-white/45 font-condensed uppercase tracking-[0.15em] truncate">{g.away}</div>
                            )}
                          </div>
                          <TeamLogoSmall logo={teams.awayResolved ? getLogo(teams.away) : null} />
                        </div>
                        <ScoreCellMobile score={s} />
                        <div className="flex-1 min-w-0 flex items-center gap-2">
                          <TeamLogoSmall logo={teams.homeResolved ? getLogo(teams.home) : null} />
                          <div className="min-w-0">
                            <div className="text-[10px] text-white/40 uppercase tracking-wider">Local</div>
                            <div className={`font-condensed text-sm truncate ${teams.homeResolved ? "text-white" : "text-white/55 italic"}`}>{teams.home}</div>
                            {teams.homeResolved && g.home !== teams.home && (
                              <div className="text-[9px] text-white/45 font-condensed uppercase tracking-[0.15em] truncate">{g.home}</div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-white/10 text-xs text-white/60 flex items-center gap-2 flex-wrap">
                        <MapPin className="w-3 h-3 text-[#E60012]" /> {g.ice}
                        {g.phase && <span className="ml-auto text-[#E60012] font-condensed uppercase tracking-wider">{g.phase}</span>}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="text-center py-20 text-white/50 font-condensed uppercase tracking-[0.2em]">
            Aucune partie ne correspond à ces filtres.
          </div>
        )}
      </section>
    </div>
  );
};

const ScoreCell = ({ score }) => {
  if (!score || score.away_score == null || score.home_score == null) {
    return <div className="text-center text-[#E60012] font-display text-xl">VS</div>;
  }
  return (
    <div className="text-center font-display text-2xl tracking-wider flex items-center justify-center gap-2">
      <span className="text-white">{score.away_score}</span>
      <span className="text-white/30">—</span>
      <span className="text-white">{score.home_score}</span>
      {score.status === "live" && <LiveBadge size="sm" className="ml-1" />}
      {score.status === "final" && (
        <span className="ml-1 px-1.5 py-0.5 bg-white/10 text-white/70 text-[9px] uppercase tracking-wider font-condensed">
          Final
        </span>
      )}
    </div>
  );
};

const ScoreCellMobile = ({ score }) => {
  if (!score || score.away_score == null || score.home_score == null) {
    return <div className="font-display text-[#E60012] text-xl">VS</div>;
  }
  return (
    <div className="text-center">
      <div className="font-display text-xl tracking-wider whitespace-nowrap">
        {score.away_score} <span className="text-white/30 mx-0.5">—</span> {score.home_score}
      </div>
      {score.status === "live" && (
        <div className="mt-0.5 flex justify-center"><LiveBadge size="sm" /></div>
      )}
      {score.status === "final" && (
        <div className="text-[8px] uppercase tracking-wider text-white/50 font-condensed">Final</div>
      )}
    </div>
  );
};

const TeamLogoSmall = ({ logo }) => (
  logo ? (
    <img src={logo} alt="" className="w-7 h-7 object-contain shrink-0" />
  ) : (
    <div className="w-7 h-7 bg-black/40 border border-white/10 flex items-center justify-center shrink-0">
      <Shield className="w-3.5 h-3.5 text-[#E60012]/70" />
    </div>
  )
);


const FilterGroup = ({ label, options, value, onChange, testid }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
    <span className="font-condensed uppercase tracking-[0.2em] text-xs text-white/55 sm:w-24 shrink-0">
      {label}
    </span>
    <div className="flex items-center gap-2 flex-wrap">
      {options.map((opt) => (
        <button
          key={opt}
          data-testid={`${testid}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
          onClick={() => onChange(opt)}
          className={`px-3.5 py-2 text-xs font-condensed uppercase tracking-[0.15em] transition-colors ${
            value === opt
              ? "bg-[#E60012] text-white"
              : "bg-transparent border border-white/15 text-white/70 hover:border-[#E60012] hover:text-white"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  </div>
);

export default Horaire;
